import { Controller } from "@hotwired/stimulus"
import { savePlan, isNotAttendingSchool } from "plan_storage"
import { renderIcons } from "lucide_icons"

// Day-in-Life toggles controller — wires the three iframe-editor
// conveniences that were deferred in the initial DIL port:
//
//   1. "Does not go to school" — hides the whole School Time block and
//      makes the weekday structure match the weekend: the After School
//      slot is relabeled "Home & Community" (with the weekend's home
//      icon). Stored at plan.dayInLife.notAttendingSchool.
//
//   2. "Same as weekday" per weekend slot — when on, the weekend
//      slot mirrors its weekday counterpart in the preview/PDF.
//      Stored at plan.meta.weekendSameAsWeekday[<weekend field>] = true.
//
//   3. "Copy from weekday" — bulk-copies the four weekday slots to
//      their weekend equivalents.

const STORAGE_KEY = "pcp_plan_data"
const WEEKEND_MIRRORS = {
  weekendMorning:  "morning",
  weekendDay:      "afternoon",  // weekend home/community ≈ weekday after-school
  weekendNight:    "night",
  weekendAfterBed: "afterBed"
}

export default class extends Controller {
  static targets = ["notSchoolToggle", "schoolWrap", "sameToggle"]

  connect() {
    this.plan = this._load()
    if (!this.plan.dayInLife) this.plan.dayInLife = {}
    if (!this.plan.meta) this.plan.meta = {}
    if (!this.plan.meta.weekendSameAsWeekday) this.plan.meta.weekendSameAsWeekday = {}
    this._reflect()
    // "Does not go to school" can also come from My School Details
    // (about.schoolDetails). Re-reflect when that — or the DIL flag — changes
    // so School Time hides / After School relabels without a reload.
    this._onPlanChange = (e) => {
      const p = e?.detail?.path || ""
      if (p === "*" || p === "about.schoolDetails" || p.startsWith("about.schoolDetails.") ||
          p === "dayInLife.notAttendingSchool") {
        this.plan = e?.detail?.plan || this._load()
        this._reflect()
      }
    }
    document.addEventListener("plan:change", this._onPlanChange)
  }

  disconnect() {
    if (this._onPlanChange) document.removeEventListener("plan:change", this._onPlanChange)
  }

  toggleNotAttending(event) {
    this.plan = this._load()
    if (!this.plan.dayInLife) this.plan.dayInLife = {}
    // Honor the checkbox's new state as the intent. (If School Details forces
    // "does not go to school", _reflect will keep it checked — manage it there.)
    this.plan.dayInLife.notAttendingSchool = event && event.target
      ? !!event.target.checked
      : !isNotAttendingSchool(this.plan)
    this._save()
    this._reflect()
    this._dispatch("dayInLife.notAttendingSchool")
  }

  toggleSame(event) {
    this.plan = this._load()
    if (!this.plan.meta) this.plan.meta = {}
    if (!this.plan.meta.weekendSameAsWeekday) this.plan.meta.weekendSameAsWeekday = {}
    const which = event.currentTarget.dataset.weekendField
    if (!which) return
    this.plan.meta.weekendSameAsWeekday[which] = !this.plan.meta.weekendSameAsWeekday[which]
    this._save()
    this._reflect()
    this._dispatch(`meta.weekendSameAsWeekday.${which}`)
  }

  copyFromWeekday() {
    if (!window.confirm("Replace all four weekend slot lists with their weekday equivalents?")) return
    this.plan = this._load()
    if (!this.plan.dayInLife) this.plan.dayInLife = {}
    for (const [weekend, weekday] of Object.entries(WEEKEND_MIRRORS)) {
      this.plan.dayInLife[weekend] = (this.plan.dayInLife[weekday] || []).map(it => ({
        ...it,
        id: Date.now() + Math.random()  // fresh IDs so dynamic-list doesn't dedupe
      }))
    }
    this._save()
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true, detail: { path: "dayInLife", plan: this.plan }
    }))
    // Force a hard re-hydrate of all dynamic-list controllers on the page.
    document.querySelectorAll('[data-controller~="dynamic-list"]').forEach(el => {
      el.dispatchEvent(new Event("plan:rehydrate"))
    })
    setTimeout(() => location.reload(), 400)
  }

  _reflect() {
    const notSchool = isNotAttendingSchool(this.plan)
    if (this.hasNotSchoolToggleTarget) this.notSchoolToggleTarget.checked = notSchool
    if (this.hasSchoolWrapTarget) this.schoolWrapTarget.classList.toggle("dil-not-school", notSchool)
    // Relabel / re-icon the After-School slot so the weekday matches the
    // weekend's "Home & Community" bucket when the participant doesn't
    // attend school. The label text lives in a .dl-label-text span inside
    // the dynamic-list header; the icon is swapped from sunset → home.
    const after = this.element.querySelector('[data-dynamic-list-path-value="dayInLife.afternoon"]')
    if (after) {
      const txt = after.querySelector(".dl-label-text")
      if (txt) txt.textContent = notSchool ? "Home & Community" : "After School"
      this._swapIcon(after, notSchool ? "home" : "sunset")
    }
    const mirror = this.plan.meta?.weekendSameAsWeekday || {}
    this.sameToggleTargets.forEach(box => {
      const which = box.dataset.weekendField
      box.checked = !!mirror[which]
    })
  }

  // Replace the dynamic-list header icon (a rendered <svg class="lucide …">
  // or an un-hydrated <i data-lucide>) with `name`, then re-render it.
  _swapIcon(listEl, name) {
    const label = listEl.querySelector("label")
    if (!label) return
    const cur = label.querySelector("svg.lucide, i[data-lucide]")
    if (!cur) return
    if (cur.classList && cur.classList.contains(`lucide-${name}`)) return
    if (cur.tagName === "I" && cur.getAttribute("data-lucide") === name) return
    const i = document.createElement("i")
    i.setAttribute("data-lucide", name)
    i.setAttribute("class", "w-4 h-4 mr-2 text-primary flex-none")
    cur.replaceWith(i)
    renderIcons(label)
  }

  _dispatch(path) {
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true, detail: { path, plan: this.plan }
    }))
  }

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) { return {} }
  }
  _save() {
    savePlan(this.plan)
  }
}
